import {
  AssignedContent,
  Assignment,
  AssignmentPayload,
  AssignmentStatus,
  AssignmentsWorkTypeMap,
  CardstackAssignment,
  CurriculumAssignmentContent,
  DesmosAssignment,
  DesmosAssignmentContent,
  MSAssignmentsStatusOptions,
  SingleAssignment,
  StudentStatus,
} from 'src/interfaces/assignment.types';
import {
  LessonContent,
  TaughtLessonAssignment,
} from 'src/interfaces/class-stats.types';
import AuthService from 'src/services/api/auth.service';
import { filterFalsy } from 'src/utils/array.utils';
import { endOfDay } from 'src/utils/date.utils';
import { pipe } from 'src/utils/pipe';

import { isCKLAPilotDomainDisabled } from './date-based-feature-flags';

export const getAssignmentLessonNumber = (lessonTitle?: string) => {
  const number = lessonTitle?.split(':');
  return number?.[1] ? number[0] : lessonTitle;
};

export const getAssignmentLessonTitle = (lessonTitle?: string) => {
  if (!lessonTitle) return lessonTitle;

  const split = lessonTitle.split(':');

  return (split[1] ? split.slice(1).join(':') : split[0]).trim();
};

export const getAssignmentTitle = (title?: string) => title?.split(':')[0];

export const getCardstackAssignmentSubTitle = (
  assignmentContent: CurriculumAssignmentContent,
) => {
  const { lessonTitle, unitTitle, title } = assignmentContent;

  const assignmentTitle = title ?? lessonTitle;

  return filterFalsy([
    getAssignmentTitle(unitTitle),
    '•',
    assignmentTitle,
  ]).join(' ');
};

export const getSortedCardstackActivities = (
  activities: CurriculumAssignmentContent[],
) =>
  [...activities].sort(
    (a, b) => (a.cardstackOrdinalNumber ?? 0) - (b.cardstackOrdinalNumber ?? 0),
  );

export const getAssignmentDomain = (domain?: string) =>
  domain
    ? domain.replace('Elementary', '').replace('Middle School', '').trim()
    : domain;

export const getAssignmentSubTitle = (
  assignmentContent: CurriculumAssignmentContent,
) => {
  const { lessonTitle, customContentId, unitTitle } = assignmentContent;
  const lessonNumber = getAssignmentLessonNumber(lessonTitle);
  const assignmentUnitTitle = getAssignmentTitle(unitTitle);

  if (customContentId) {
    return assignmentUnitTitle
      ? ['•', assignmentUnitTitle].join(' ')
      : assignmentUnitTitle;
  }

  return filterFalsy(['•', assignmentUnitTitle, '•', lessonNumber]).join(' ');
};

export const getContentStatus = (
  userBk: string,
  contentBk: string,
  studentStatuses?: StudentStatus[],
) => {
  const currentStudentStatus = studentStatuses?.find(
    ({ studentBusinessKey }) => studentBusinessKey === userBk,
  );

  const contentStatus = currentStudentStatus?.contentStatuses?.find(
    ({ contentBusinessKey }) => contentBusinessKey === contentBk,
  );

  return contentStatus?.status ?? AssignmentStatus.TODO;
};

export const getAssignmentContent = (
  assignment: Assignment,
): CurriculumAssignmentContent => {
  if (assignment.type === 'Cardstack') return assignment.content[0];

  return assignment.content;
};

export const getAssignmentThumbnailPath = (
  content:
    | CurriculumAssignmentContent
    | DesmosAssignmentContent
    | LessonContent,
) => {
  if (isDesmosAssignmentContent(content)) return content.thumbURL;

  return content.lessonThumbnailImagePath;
};

type ContinueButtonCheckParams = {
  activeAssignmentsFilter: MSAssignmentsStatusOptions | null;
  assignmentStatus?: AssignmentStatus;
};

export const getShouldShowOpenButton = ({
  activeAssignmentsFilter,
  assignmentStatus,
}: ContinueButtonCheckParams) => {
  return activeAssignmentsFilter === null && assignmentStatus
    ? MSAssignmentsStatusOptions.STARTED.includes(assignmentStatus) ||
        MSAssignmentsStatusOptions.HANDED_IN.includes(assignmentStatus)
    : activeAssignmentsFilter === MSAssignmentsStatusOptions.STARTED;
};

export const getMSAssignmentStatusOption = (
  assignmentStatus = AssignmentStatus.TODO,
) => {
  if (
    assignmentStatus === AssignmentStatus.READ ||
    assignmentStatus === AssignmentStatus.STARTED
  )
    return MSAssignmentsStatusOptions.STARTED;

  if (assignmentStatus === AssignmentStatus.HANDED_IN)
    return MSAssignmentsStatusOptions.HANDED_IN;

  return MSAssignmentsStatusOptions.NOT_STARTED;
};

// check if content includes desmos-specific fields, that are being in use
export const isDesmosAssignmentContent = (
  content: CurriculumAssignmentContent | DesmosAssignmentContent,
): content is DesmosAssignmentContent => {
  return (
    'activityLandingPageURL' in content ||
    'studentGreetingURL' in content ||
    'studentURL' in content
  );
};

export const normalizeDesmosContent = (content: {
  content: CurriculumAssignmentContent | DesmosAssignmentContent;
  type?: string;
}) => {
  if (
    !content.content.domain ||
    content.content.domain.toLowerCase().includes('desmos')
  ) {
    return {
      ...content.content,
      domain: 'Math',
      contentType: content.type,
    };
  }

  return {
    ...content.content,
    contentType: content.type,
    domain: normalizeDomain(content.content.domain),
  };
};

export const isActivityDisplayed = (
  displayedAssignments: Assignment[],
  activityBk: string,
) =>
  displayedAssignments.some((assignment) => {
    if (assignment.type === 'TaughtLessonAssignment') return false;

    const assignmentContent = assignment.content;

    if (Array.isArray(assignmentContent)) {
      return assignmentContent.some(
        (contentItem) =>
          (contentItem.cardstackKey ?? contentItem.lessonKey) === activityBk,
      );
    }

    if (isDesmosAssignmentContent(assignmentContent)) {
      return assignmentContent.activityId === activityBk;
    }

    return (
      (assignmentContent.cardstackKey ?? assignmentContent.lessonKey) ===
      activityBk
    );
  });

/**
 * For assignments of enrollment type 'roster' returns value of assignment's startDate or null.
 *
 * For assignments of enrollment type 'single_session' returns value of createdDate
 * in status object from studentStatuses set for current user.
 *
 * Such approach is required for correct sorting of single sessions code assignments for students
 * since they will see such assignments only after they started it.
 * */
const getAssignmentStartDate = (
  assignment: AssignmentPayload,
): number | null => {
  const { studentStatuses, startDate, enrollmentType } = assignment;

  if (enrollmentType !== 'single_session' || !studentStatuses?.length) {
    return startDate ?? null;
  }

  const userBk = AuthService.getUserBk();

  const userAssignmentStatus = studentStatuses.find(
    (status) => status.studentBusinessKey === userBk,
  );

  if (!userAssignmentStatus) {
    return startDate ?? null;
  }

  return userAssignmentStatus.createdDate;
};

const normalizeDomain = (domain?: string) => {
  return isCKLAPilotDomainDisabled() && domain === 'CKLA Pilot'
    ? 'CKLA'
    : domain;
};

const normalizeSingleAssignmentContent = (assignedContent: AssignedContent) => {
  return isDesmosAssignmentContent(assignedContent.content)
    ? normalizeDesmosContent(assignedContent)
    : {
        ...assignedContent.content,
        domain: normalizeDomain(assignedContent.content.domain),
      };
};

export const normalizeData = (
  assignments: (AssignmentPayload | TaughtLessonAssignment)[],
  userBk: string,
): Assignment[] => {
  return assignments
    .map((assignment: AssignmentPayload | TaughtLessonAssignment) => {
      if (
        'type' in assignment &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        assignment.type === 'TaughtLessonAssignment'
      ) {
        return assignment;
      }
      if (
        'assignedContent' in assignment &&
        assignment.assignedContent[0].type === 'Cardstack'
      ) {
        const content = assignment.assignedContent.reduce((prev, item) => {
          const itemContent = {
            ...item.content,
            domain: normalizeDomain(item.content.domain),
            contentBusinessKey: item.contentBusinessKey,
          };

          prev.push(itemContent);

          return prev;
        }, [] as CurriculumAssignmentContent[]);

        return {
          assignmentBk: assignment.businessKey,
          assignmentClassBks: assignment.classBks,
          contentBk: assignment.assignedContent[0].contentBusinessKey,
          createdDate: assignment.createdDate,
          startDate: assignment.startDate ?? null,
          lastUpdated: assignment.lastUpdated,
          dueDate: assignment.dueDate ?? null,
          content,
          status: AssignmentStatus.TODO,
          type: 'Cardstack',
          studentStatuses: assignment.studentStatuses,
        } as Assignment;
      }

      return 'assignedContent' in assignment
        ? assignment.assignedContent.map(
            (content: AssignedContent) =>
              ({
                assignmentBk: assignment.businessKey,
                assignmentClassBks: assignment.classBks,
                contentBk: content.contentBusinessKey,
                createdDate: assignment.createdDate,
                startDate: getAssignmentStartDate(assignment),
                lastUpdated: assignment.lastUpdated,
                dueDate: assignment.dueDate ?? null,
                content: normalizeSingleAssignmentContent(content),
                status: getContentStatus(
                  userBk,
                  content.contentBusinessKey,
                  assignment.studentStatuses,
                ),
                type: 'Lesson',
                enrollmentType: assignment.enrollmentType,
                isHandedInmCLASSAssessment:
                  getContentStatus(
                    userBk,
                    content.contentBusinessKey,
                    assignment.studentStatuses,
                  ) === AssignmentStatus.HANDED_IN &&
                  content.metadata?.ismCLASSAssessment,
              } as Assignment),
          )
        : [];
    })
    .flat();
};

export const getIsAssignmentDueDateExpired = (
  assignment: SingleAssignment | CardstackAssignment | DesmosAssignment,
) => {
  const endOfCreatedDayTimestamp = endOfDay(assignment.createdDate);
  const currentDateTime = new Date().getTime();

  // Consider expired when it doesn't have dueDate after the end of the created day has passed.
  if (!assignment.dueDate) {
    return currentDateTime > endOfCreatedDayTimestamp;
  }

  return currentDateTime > assignment.dueDate;
};

export const getAssignmentSortingDate = (assignment: Assignment) => {
  if (assignment.type === 'TaughtLessonAssignment')
    return assignment.createdDatetime;

  if (assignment.type === 'Cardstack') {
    return !assignment.startDate ||
      assignment.lastUpdated >= assignment.startDate
      ? assignment.lastUpdated
      : assignment.startDate;
  }

  return assignment.startDate ?? assignment.createdDate;
};

export const sortAssignmentsByStartDate = (
  assignments: Assignment[],
): Assignment[] =>
  assignments.slice().sort((a, b) => {
    const aStartDate = getAssignmentSortingDate(a);
    const bStartDate = getAssignmentSortingDate(b);

    return bStartDate - aStartDate;
  });

export const sortAssignments = (assignments: Assignment[]): Assignment[] => {
  return sortAssignmentsByStartDate(assignments);
};

export const getPendingOnlyAssignments = (
  assignments: Assignment[],
): Assignment[] => {
  return assignments.filter((assignment) => {
    if (!assignment.startDate) return true;

    const currentDateTime = new Date().getTime();

    return currentDateTime > assignment.startDate;
  });
};

export const getRelevantWorkTypeForQuery = (
  workType: AssignmentsWorkTypeMap,
) => {
  // APPS enum value is already 'TODO', so just return workType as-is
  return workType;
};

// @TODO: remove getPendingOnlyAssignments in HOMES-1662
export const prepareAssignments = pipe(
  normalizeData,
  getPendingOnlyAssignments,
  sortAssignments,
);
